<template>
  <div class="edit--preset">
    <QuickNavigation
      :options="options"
      :default-option-drop="defaultTargetComponent"
      @on-change-option="onChangeOption"
      @back="onBack" />
    <div id="text-advanced-setting" teleport></div>
    <component :is="component" v-if="component" @is-ready-to-save="isReadyToSave = $event"></component>
    <!-- auto save in currentPreset -->
    <!-- <div class="footer">
      <button class="btn-cancel" @click="handleCancel" v-popover:tooltip.top="'Dismiss changes'">Cancel</button>
      <ProcessButton @click="handleSaveData" :status.sync="processStatus" :buttonStyling="processButtonStyle" v-popover:tooltip.top="'Save changes'">
        <template #loading>Applying</template>
        <template #success>Success</template>
        <template #error>Error</template>
        <template #default>Save Settings</template>
      </ProcessButton>
    </div> -->
  </div>
</template>

<script lang="ts">
import { mapActions, mapMutations, mapState } from 'vuex';
import { defineComponent } from 'vue';
import ProcessButton from '@/core/components/ProcessButton.vue';
import { mutations } from '@modules/editor-sidebar';
import { cloneDeep } from '@/utils/common';
import { generateCssString } from '@modules/global-style';
import { $jQuery } from '@/app/index';
import { actions as commonActions } from '@/store/common/types';
import { EDITOR_EVENT } from '@/app/editor/config/constants/trackEventIds';
import { presetOptionTypes } from '../constants/styleConstant';
import QuickNavigation from './common/QuickNavigation.vue';
import { dropPresetStyleOptions, dropPresetValueStyle } from '../constants/styleConstant';
import FooterPresetOption from '../utils/mixins/FooterPresetOption';
import ColorComponent from './packs/Color.vue';
import BorderComponent from './packs/Border.vue';
import TextComponent from './packs/Text.vue';
import ButtonComponent from './packs/Button.vue';
import CornerComponent from './packs/Corner.vue';
import SpacingComponent from './packs/Spacing.vue';

interface DropdownOptionInterface {
  icon: string;
  name: string;
  value: string;
}

interface DataInterface {
  component: any;
  options: any;
  processButtonStyle: object;
  isReadyToSave: boolean;
}

export default defineComponent({
  name: 'Preset',
  components: {
    QuickNavigation,
    ProcessButton,
  },
  mixins: [FooterPresetOption],
  props: {
    defaultTargetComponent: {
      type: String,
      default: dropPresetValueStyle.colorStyle,
    },
  },
  emits: ['changeTargetComponent', 'backLiveThemePreset'],
  data: (): DataInterface => ({
    component: '',
    options: dropPresetStyleOptions,
    processButtonStyle: {
      width: '45%',
      'margin-left': '4px',
      'margin-right': '12px',
      'font-size': '12px',
    },
    isReadyToSave: true,
  }),
  computed: {
    ...mapState('sidebar', {
      currentPreset: (state) => (state as any).currentPreset,
      nextPreset: (state) => (state as any).nextPreset,
      prevPreset: (state) => (state as any).prevPreset,
      previewPreset: (state) => (state as any).previewPreset,
      currentPresetOptions: (state) => (state as any).currentPresetOptions,
    }),
  },
  watch: {
    nextPreset(value) {
      if (value.type) {
        (this as any).checkComponentType(value.type);
        this.$emit('changeTargetComponent', value.type);
        setTimeout(() => {
          (this as any).setStyleIdTargetGS(value.target);
        }, 100);
        (this as any).handleSaveDataChangeComponentStyle();
      }
    },
    currentPresetOptions: {
      deep: true,
      handler: function () {
        const preset = cloneDeep(this.currentPreset);
        switch (this.defaultTargetComponent) {
          case presetOptionTypes.color:
            preset.presetOptions.colors = this.currentPresetOptions;
            break;
          case presetOptionTypes.text:
            preset.presetOptions.texts = this.currentPresetOptions;
            break;
          case presetOptionTypes.corner:
            preset.presetOptions.corners = this.currentPresetOptions;
            break;
          case presetOptionTypes.spacing:
            preset.presetOptions.spacings = this.currentPresetOptions;
            break;
          case presetOptionTypes.border:
            preset.presetOptions.borders = this.currentPresetOptions;
            break;
          case presetOptionTypes.button:
            preset.presetOptions.buttons = this.currentPresetOptions;
            break;
          default:
            break;
        }
        this.setCurrentPreset(preset);
        if (this.previewPreset) {
          const cssString = generateCssString(preset);
          const $gfFrameContents = $jQuery('#gfFrame').contents();
          $gfFrameContents.find('.gryffeditor .gf_gs-global-style-preview').html(cssString);
        }
      },
    },
  },
  mounted() {
    this.trackEvent({ eventId: EDITOR_EVENT.GS_STYLE_DETAIL_VIEWD, properties: { type: this.defaultTargetComponent } });

    return (this as any).checkComponentType(this.defaultTargetComponent || '');
  },
  methods: {
    ...mapMutations('sidebar', {
      stepPreset: mutations.SET_STEP_PRESET,
      setPresetTypeId: mutations.SET_PRESET_TYPE_ID,
      setComponentTargetGS: mutations.SET_COMPONENT_TARGET_GLOBAL_STYLE,
      setStyleIdTargetGS: mutations.SET_STYLE_ID_TARGET_GLOBAL_STYLE,
      setCurrentPreset: mutations.SET_CURRENT_PRESET,
    }),
    ...mapActions('common', {
      trackEvent: commonActions.TRACK_EVENT,
    }),
    onBack() {
      if (!this.isReadyToSave) {
        return;
      }
      if (this.prevPreset && this.prevPreset.type) {
        (this as any).checkComponentType(this.prevPreset.type);
        this.$emit('changeTargetComponent', this.prevPreset.type);
        setTimeout(() => {
          (this as any).setStyleIdTargetGS(this.prevPreset.target);
          (this as any).stepPreset({ next: {}, prev: {} });
        }, 0);
      } else {
        this.$emit('backLiveThemePreset');
        (this as any).setStyleIdTargetGS(0);
      }
      (this as any).handleSaveDataChangeComponentStyle();
    },
    checkComponentType(type: string) {
      switch (type) {
        case dropPresetValueStyle.colorStyle:
          this.component = ColorComponent as any;
          break;
        case dropPresetValueStyle.textStyle:
          this.component = TextComponent as any;
          return;
        case dropPresetValueStyle.borderStyle:
          this.component = BorderComponent as any;
          break;
        case dropPresetValueStyle.cornerStyle:
          this.component = CornerComponent as any;
          return;
        case dropPresetValueStyle.spacingStyle:
          this.component = SpacingComponent as any;
          return;
        case dropPresetValueStyle.buttonStyle:
          this.component = ButtonComponent as any;
          return;
        default:
      }
    },
    onChangeOption(option: DropdownOptionInterface): void {
      if (!this.isReadyToSave) {
        return;
      }
      this.trackEvent({ eventId: EDITOR_EVENT.GS_QUICK_NAV_CLICKED });

      if (option.value !== this.defaultTargetComponent) {
        (this as any).handleSaveDataChangeComponentStyle();
        (this as any).setPresetTypeId(option.value);
        this.$emit('changeTargetComponent', option.value);
        return (this as any).checkComponentType(option.value);
      }
    },
    handleSaveDataChangeComponentStyle() {
      let originPresetOptions = null;
      switch (this.defaultTargetComponent) {
        case presetOptionTypes.color:
          originPresetOptions = this.currentPreset.presetOptions?.colors;
          break;
        case presetOptionTypes.text:
          originPresetOptions = this.currentPreset.presetOptions?.texts;
          break;
        case presetOptionTypes.corner:
          originPresetOptions = this.currentPreset.presetOptions?.corners;
          break;
        case presetOptionTypes.spacing:
          originPresetOptions = this.currentPreset.presetOptions?.spacings;
          break;
        case presetOptionTypes.border:
          originPresetOptions = this.currentPreset.presetOptions?.borders;
          break;
        case presetOptionTypes.button:
          originPresetOptions = this.currentPreset.presetOptions?.buttons;
          break;
        default:
          break;
      }
      if (JSON.stringify(originPresetOptions) !== JSON.stringify(this.currentPresetOptions)) {
        this.handleSaveData();
      }
    },
  },
});
</script>
<style lang="scss">
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
.edit--preset {
  height: 100%;
  color: white;
}

.footer {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  position: absolute;
  bottom: 50px;
  width: 100%;
  height: 50px;
  background: map-get($hybrid-shade-dark, '400');

  .btn-cancel {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    position: static;
    height: 32px;
    border: none;
    box-sizing: border-box;
    border-radius: 3px;
    flex: none;
    flex-grow: 0;
    color: #fff;
    width: 44%;
    background: map-get($hybrid-shade-dark, '300');
    margin-left: 12px;
    margin-right: 4px;

    &:hover {
      background: map-get($hybrid-shade-dark, '200');
    }
  }

  .btn-save {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    position: static;
    height: 32px;
    border: none;
    box-sizing: border-box;
    border-radius: 3px;
    flex: none;
    flex-grow: 0;
    color: #fff;
    background: map-get($primary-blue-ui-kit, '300');
    width: 48%;

    &:hover {
      background: map-get($primary-blue-ui-kit, '325');
    }
  }
}
</style>
